import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Handshake, Copy, Check, Trash2, Pause, Play } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  active: "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10",
  paused: "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10",
  pending: "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/10",
};

export default function PartnersPanel() {
  const [partners, setPartners] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", credit: 5 });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const origin = API_BASE.replace(/\/api$/, "");

  const load = async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners(data);
    } catch { setPartners([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Partner name is required"); return; }
    setBusy(true);
    try {
      await api.post("/partners", {
        name: form.name,
        email: form.email || null,
        credit_per_signup_usd: Number(form.credit) || 5,
      });
      setForm({ name: "", email: "", credit: 5 });
      toast.success("Partner created — share their tracked link");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create partner");
    } finally { setBusy(false); }
  };

  const copyLink = async (code) => {
    const link = `${origin}/api/partners/r/${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(code);
      toast.success("Partner link copied");
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Copy failed — link: " + link);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/partners/${id}`, { status });
      toast.success(`Partner ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this partner and their link permanently?")) return;
    try {
      await api.delete(`/partners/${id}`);
      toast.success("Partner deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="partners-panel">
      <div className="flex items-center gap-2">
        <Handshake className="h-4 w-4 text-[#34C759]" />
        <h2 className="font-black text-xl tracking-tight">Affiliate partners</h2>
        {partners && <span className="text-xs text-neutral-500 font-mono">({partners.length})</span>}
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Each partner gets a unique tracked link. Clicks and signups attribute automatically — credit owed shows what you owe them.
      </p>

      <div className="mt-4 grid md:grid-cols-4 gap-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Partner name"
          data-testid="partner-name-input"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#34C759]" />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)"
          data-testid="partner-email-input"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#34C759]" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-400">$</span>
          <input type="number" min={0} max={100} value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })}
            data-testid="partner-credit-input"
            className="h-10 w-20 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-[#34C759]" />
          <span className="text-xs text-neutral-400">/ signup</span>
        </div>
        <button onClick={create} disabled={busy} data-testid="partner-create-btn"
          className="h-10 rounded-full bg-[#34C759] text-black font-bold text-sm hover:bg-[#2eb350] transition-colors disabled:opacity-50">
          {busy ? "Creating…" : "Add partner"}
        </button>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="partners-table">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left py-2 pr-3">Partner</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-right py-2 pr-3">Clicks</th>
              <th className="text-right py-2 pr-3">Signups</th>
              <th className="text-right py-2 pr-3">Credit owed</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners === null ? (
              <tr><td colSpan={6} className="py-4 text-neutral-500">Loading…</td></tr>
            ) : partners.length === 0 ? (
              <tr><td colSpan={6} className="py-4 text-neutral-500">No partners yet — add your first promoter above.</td></tr>
            ) : partners.map((p) => (
              <tr key={p.id} className="border-t border-[#27272A]" data-testid={`partner-row-${p.code}`}>
                <td className="py-2.5 pr-3">
                  <div className="text-white font-medium">{p.name}</div>
                  <div className="text-xs text-neutral-500 font-mono">{p.email || p.code}</div>
                  {p.channel && <div className="text-[11px] text-neutral-600 mt-0.5 max-w-[240px] truncate" title={p.channel}>{p.channel}</div>}
                </td>
                <td className="py-2.5 pr-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[p.status] || STATUS_STYLE.paused}`}>{p.status}</span>
                </td>
                <td className="py-2.5 pr-3 text-right font-mono">{p.clicks}</td>
                <td className="py-2.5 pr-3 text-right font-mono">{p.signups}</td>
                <td className="py-2.5 pr-3 text-right font-mono text-[#34C759]">${p.credit_owed_usd.toFixed(2)}</td>
                <td className="py-2.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {p.status === "pending" ? (
                      <button onClick={() => setStatus(p.id, "active")} data-testid={`partner-approve-${p.code}`}
                        className="h-7 px-2.5 rounded-full bg-[#34C759] text-black text-[11px] font-bold uppercase tracking-wider hover:bg-[#2eb350] transition-colors">
                        Approve
                      </button>
                    ) : (
                      <IconBtn testid={`partner-copy-${p.code}`} onClick={() => copyLink(p.code)} icon={copied === p.code ? Check : Copy} title="Copy tracked link" />
                    )}
                    {p.status === "active"
                      ? <IconBtn testid={`partner-pause-${p.code}`} onClick={() => setStatus(p.id, "paused")} icon={Pause} title="Pause" />
                      : p.status === "paused"
                        ? <IconBtn testid={`partner-activate-${p.code}`} onClick={() => setStatus(p.id, "active")} icon={Play} title="Activate" />
                        : null}
                    <IconBtn testid={`partner-delete-${p.code}`} onClick={() => remove(p.id)} icon={Trash2} title="Delete" red />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IconBtn({ onClick, icon: Icon, title, red, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`h-7 w-7 inline-flex items-center justify-center rounded-full border border-[#27272A] text-neutral-400 transition-colors ${red ? "hover:border-[#FF3B30] hover:text-[#FF3B30]" : "hover:border-white hover:text-white"}`}>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
